import React from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FiUsers,
  FiHeart,
  FiMic,
  FiBook,
  FiAward,
  FiCheck,
} from "react-icons/fi";
import { BiBrain } from "react-icons/bi";
import { HiOutlineUserGroup } from "react-icons/hi";
import { IoGameControllerOutline } from "react-icons/io5";

// İkon mapping
const iconMap = {
  "fa fa-users": HiOutlineUserGroup,
  "fa fa-child": FiHeart,
  "fa fa-microphone": FiMic,
  "fa fa-book": FiBook,
  "fa fa-gamepad": IoGameControllerOutline,
  "fa fa-award": FiAward,
  "fa fa-brain": BiBrain,
  brain: BiBrain,
  users: HiOutlineUserGroup,
  gamepad: IoGameControllerOutline,
  heart: FiHeart,
};

// Varsayılan hizmet verileri (eğer data gelmezse veya details yoksa)
const defaultServices = [
  {
    icon: "fa fa-users",
    name: "Aile Danışmanlığı",
    description:
      "Ebeveynlere çocuk gelişimi konusunda profesyonel rehberlik ve destek hizmeti",
    details: [
      "Birebir danışmanlık seansları",
      "Ebeveyn eğitim programları",
      "Davranış yönetimi stratejileri",
      "Aile dinamikleri analizi",
    ],
    onClick: "/aile-danismanligi",
  },
  {
    icon: "fa fa-brain",
    name: "Gelişimsel Değerlendirme",
    description:
      "Çocuğunuzun bilişsel, fiziksel, sosyal ve duygusal gelişiminin kapsamlı değerlendirmesi",
    details: [
      "Kapsamlı gelişim taraması",
      "Standartlaştırılmış testler",
      "Detaylı rapor ve öneriler",
      "Bireysel gelişim planı",
    ],
    onClick: "/dirfloortime-201-terapisti",
  },
  {
    icon: "fa fa-gamepad",
    name: "Oyun Terapisi",
    description:
      "Çocukların duygusal ve sosyal gelişimlerini destekleyen terapötik oyun seansları",
    details: [
      "Yaşa uygun terapi teknikleri",
      "Duygusal ifade geliştirme",
      "Sosyal beceri kazandırma",
      "Travma sonrası destek",
    ],
    onClick: "/oyun-terapisi",
  },
  {
    icon: "fa fa-microphone",
    name: "Diksiyon Eğitimi",
    description:
      "Etkili konuşma ve iletişim becerilerini geliştiren profesyonel eğitim programı",
    details: [
      "Ses ve nefes teknikleri",
      "Artikülasyon çalışmaları",
      "Özgüven geliştirme",
      "Topluluk önünde konuşma",
    ],
    onClick: "/diksiyon-ve-etkili-konusma",
  },
  {
    icon: "fa fa-book",
    name: "Eğitici Eğitimi",
    description:
      "Eğitimcilere yönelik profesyonel gelişim ve sertifika programları",
    details: [
      "Pedagojik yaklaşımlar",
      "Sınıf yönetimi teknikleri",
      "Öğrenme stilleri analizi",
      "MEB onaylı sertifika",
    ],
    onClick: "/egitici-egitimi",
  },
];

export const Services = ({ data, seo }) => {
  const navigate = useNavigate();

  // Data yoksa veya details içermiyorsa defaultServices kullan
  const services =
    data && data.length > 0 && data[0].details ? data : defaultServices;

  // Schema.org JSON-LD for Services
  const schemaOrgJSONLD = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Çocuk Gelişim Danışmanlığı",
    provider: {
      "@type": "ProfessionalService",
      name: "Dilan Aslan | Çocuk Gelişim Danışmanlığı",
      image: "https://www.dilanaslan.com/img/intro-bg11.jpg",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Artuklu",
        addressLocality: "Mardin",
        addressRegion: "Mardin",
        postalCode: "47200",
        addressCountry: "TR",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: "37.3212",
        longitude: "40.7245",
      },
      telephone: "+905055517747",
      url: "https://www.dilanaslan.com",
    },
    areaServed: {
      "@type": "City",
      name: "Mardin",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Çocuk Gelişim Hizmetleri",
      itemListElement: [
        {
          "@type": "OfferCatalog",
          name: "Aile Danışmanlığı",
          description:
            "Ebeveynlere çocuk gelişimi konusunda profesyonel rehberlik ve destek hizmeti",
          itemListElement: [
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Birebir Danışmanlık Seansları",
              },
            },
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Ebeveyn Eğitim Programları",
              },
            },
          ],
        },
        {
          "@type": "OfferCatalog",
          name: "Gelişimsel Değerlendirme",
          description:
            "Çocuğunuzun bilişsel, fiziksel, sosyal ve duygusal gelişiminin kapsamlı değerlendirmesi",
          itemListElement: [
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Kapsamlı Gelişim Taraması",
              },
            },
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Standartlaştırılmış Testler",
              },
            },
          ],
        },
        {
          "@type": "OfferCatalog",
          name: "Oyun Terapisi",
          description:
            "Çocukların duygusal ve sosyal gelişimlerini destekleyen terapötik oyun seansları",
          itemListElement: [
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Yaşa Uygun Terapi Teknikleri",
              },
            },
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Sosyal Beceri Kazandırma",
              },
            },
          ],
        },
        {
          "@type": "OfferCatalog",
          name: "Diksiyon Eğitimi",
          description:
            "Etkili konuşma ve iletişim becerilerini geliştiren profesyonel eğitim programı",
        },
        {
          "@type": "OfferCatalog",
          name: "Eğitici Eğitimi",
          description:
            "Eğitimcilere yönelik profesyonel gelişim ve sertifika programları",
        },
      ],
    },
  };

  const handleServiceClick = (path) => {
    window.scrollTo(0, 0);
    setTimeout(() => {
      navigate(path, { replace: false });
    }, 100);
  };

  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="services"
      className="py-24 bg-gradient-to-b from-white to-blue-50"
    >
      <Helmet>
        {/* Temel Meta Etiketleri */}
        <html lang="tr" />
        <title>
          {seo?.title ||
            "Hizmetlerimiz | Dilan Aslan Çocuk Gelişim Danışmanlığı - Mardin Artuklu"}
        </title>
        <meta
          name="description"
          content={
            seo?.description ||
            "Mardin Artuklu'da profesyonel çocuk gelişim hizmetleri: Aile danışmanlığı, oyun terapisi, gelişimsel değerlendirme, diksiyon eğitimi ve eğitici eğitimi. Uzman Dilan Aslan ile çocuğunuzun potansiyelini keşfedin."
          }
        />
        <meta
          name="keywords"
          content="çocuk gelişim hizmetleri Mardin, aile danışmanlığı Artuklu, oyun terapisi Mardin, gelişimsel değerlendirme, diksiyon eğitimi, eğitici eğitimi, çocuk terapisi, ebeveyn danışmanlığı, Dilan Aslan hizmetler, çocuk psikolojisi Mardin"
        />
        <meta name="author" content="Dilan Aslan" />
        <link rel="canonical" href="https://www.dilanaslan.com/hizmetler" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://www.dilanaslan.com/hizmetler"
        />
        <meta
          property="og:title"
          content="Hizmetlerimiz | Dilan Aslan Çocuk Gelişim Danışmanlığı"
        />
        <meta
          property="og:description"
          content="Mardin Artuklu'da profesyonel çocuk gelişim hizmetleri: Aile danışmanlığı, oyun terapisi, gelişimsel değerlendirme ve daha fazlası."
        />
        <meta
          property="og:image"
          content="https://www.dilanaslan.com/img/intro-bg11.jpg"
        />
        <meta property="og:locale" content="tr_TR" />
        <meta
          property="og:site_name"
          content="Dilan Aslan Çocuk Gelişim Danışmanlığı"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:url"
          content="https://www.dilanaslan.com/hizmetler"
        />
        <meta
          name="twitter:title"
          content="Hizmetlerimiz | Dilan Aslan Çocuk Gelişim Danışmanlığı"
        />
        <meta
          name="twitter:description"
          content="Mardin Artuklu'da profesyonel çocuk gelişim hizmetleri: Aile danışmanlığı, oyun terapisi ve gelişimsel değerlendirme."
        />
        <meta
          name="twitter:image"
          content="https://www.dilanaslan.com/img/intro-bg11.jpg"
        />

        {/* Coğrafi Etiketler */}
        <meta name="geo.region" content="TR-47" />
        <meta name="geo.placename" content="Mardin, Artuklu" />
        <meta name="geo.position" content="37.3212;40.7245" />
        <meta name="ICBM" content="37.3212, 40.7245" />

        {/* Mobil Optimizasyon */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#2563EB" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta
          name="apple-mobile-web-app-status-bar-style"
          content="black-translucent"
        />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(schemaOrgJSONLD)}
        </script>

        {/* Robots */}
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
        <meta name="googlebot" content="index, follow" />
      </Helmet>

      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12">
        {/* Başlık */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Hizmetlerimiz
          </h2>
          <p className="text-lg md:text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed">
            Çocuğunuzun gelişimini desteklemek için kapsamlı ve profesyonel
            hizmetler sunuyoruz
          </p>
        </motion.div>

        {/* Hizmet Kartları */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon] || FiHeart;

            return (
              <motion.div
                key={`${service.name}-${index}`}
                className="bg-white rounded-2xl p-8 border-2 border-dashed border-gray-200 hover:border-blue-400 hover:shadow-xl transition-all duration-300 cursor-pointer group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onClick={() => handleServiceClick(service.onClick)}
              >
                {/* İkon */}
                <div className="bg-blue-600 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <IconComponent size={32} className="text-white" />
                </div>

                {/* Başlık */}
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {service.name}
                </h3>

                {/* Açıklama */}
                <p className="text-gray-500 mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Detay Listesi */}
                {service.details && service.details.length > 0 && (
                  <div className="space-y-3">
                    {service.details.map((detail, idx) => (
                      <div key={idx} className="flex items-start">
                        <div className="bg-blue-100 rounded-full p-1 mr-3 mt-0.5 flex-shrink-0">
                          <FiCheck className="w-3 h-3 text-blue-600" />
                        </div>
                        <span className="text-gray-600 text-sm">{detail}</span>
                      </div>
                    ))}
                  </div>
                )}
                <button className="mt-6 text-blue-600 font-semibold group-hover:underline">
                  Detayları Görüntüle
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* Alt CTA Bölümü */}
        <motion.div
          className="mt-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl p-12 md:p-16 text-center text-white shadow-2xl"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <h3 className="text-3xl md:text-4xl font-bold mb-6">
            Size Özel Bir Program Hazırlayalım
          </h3>
          <p className="text-xl md:text-2xl mb-10 text-blue-100 max-w-2xl mx-auto leading-relaxed">
            Çocuğunuzun ihtiyaçlarına göre özelleştirilmiş bir gelişim programı
            için bizimle iletişime geçin
          </p>
          <a
            href="https://wa.me/+905055517747"
            className="bg-white text-blue-600 px-10 py-5 rounded-xl text-lg font-semibold hover:bg-blue-50 transition-all transform hover:scale-105 shadow-lg"
          >
            Hemen Başvur
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
